import { FC, useEffect, useMemo, useRef, useState } from "react";
import { Popup, Toast } from "antd-mobile";
import {
  CloseOutlined,
  CheckCircleFilled,
  PlusCircleFilled,
  AppstoreOutlined,
} from "@ant-design/icons";
import {
  DndContext,
  closestCenter,
  PointerSensor,
  TouchSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  restrictToHorizontalAxis,
  restrictToParentElement,
} from "@dnd-kit/modifiers";
import {
  SortableContext,
  arrayMove,
  horizontalListSortingStrategy,
  useSortable,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import styles from "./styles.module.scss";

interface IProps {
  open: boolean;
  onClose: () => void;
  /** Full ordered universe of items the user can choose from. */
  allItems: ITabObject[];
  /** Currently pinned route keys (ordered). */
  pinnedKeys: string[];
  /** Max number of items allowed in the bottom bar (excludes the More button). */
  maxPinned: number;
  /** Persist the new ordered pinned keys. */
  onSave: (keys: string[]) => void;
  /** Reset to the app default layout. */
  onReset: () => void;
}

interface ISelectedCardProps {
  item: ITabObject;
  onRemove: (key: string) => void;
}

/** A single draggable card in the "Selected item" row. */
const SelectedCard: FC<ISelectedCardProps> = ({ item, onRemove }) => {
  const key = item.key as string;
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: key });

  return (
    <div
      ref={setNodeRef}
      className={`${styles.selectedCard} ${
        isDragging ? styles.selectedCardDragging : ""
      }`}
      style={{ transform: CSS.Transform.toString(transform), transition }}
      {...attributes}
      {...listeners}
    >
      <button
        type="button"
        className={styles.removeBadge}
        aria-label={`Remove ${item.title} from bottom bar`}
        onPointerDown={(e) => e.stopPropagation()}
        onClick={() => onRemove(key)}
      >
        <CloseOutlined />
      </button>
      <span className={styles.selectedIcon}>{item.icon}</span>
      <span className={styles.selectedLabel}>{item.title}</span>
    </div>
  );
};

interface IMenuItemProps {
  item: ITabObject;
  selected: boolean;
  muted: boolean;
  onToggle: (key: string) => void;
}

/** A single circular toggle in the "All menu items" grid. */
const MenuItem: FC<IMenuItemProps> = ({ item, selected, muted, onToggle }) => {
  const key = item.key as string;
  return (
    <button
      type="button"
      className={`${styles.menuItem} ${
        selected ? styles.menuItemSelected : ""
      } ${muted ? styles.menuItemMuted : ""}`}
      aria-pressed={selected}
      aria-disabled={muted}
      onClick={() => onToggle(key)}
    >
      <span className={styles.menuCircle}>
        {item.icon}
        {selected ? (
          <CheckCircleFilled className={styles.menuCheck} />
        ) : (
          !muted && <PlusCircleFilled className={styles.menuAdd} />
        )}
      </span>
      <span className={styles.menuLabel}>{item.title}</span>
    </button>
  );
};

export const MobileNavCustomizerSheet: FC<IProps> = ({
  open,
  onClose,
  allItems,
  pinnedKeys,
  maxPinned,
  onSave,
  onReset,
}) => {
  const [working, setWorking] = useState<string[]>(pinnedKeys);
  const [shake, setShake] = useState(false);
  const shakeTimer = useRef<ReturnType<typeof setTimeout>>();

  // Re-seed from the saved order each time the sheet opens.
  useEffect(() => {
    if (open) setWorking(pinnedKeys);
  }, [open, pinnedKeys]);

  useEffect(() => () => clearTimeout(shakeTimer.current), []);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 180, tolerance: 6 },
    })
  );

  const byKey = useMemo(() => {
    const map = new Map<string, ITabObject>();
    allItems.forEach((i) => map.set(i.key as string, i));
    return map;
  }, [allItems]);

  const selectedItems = useMemo(
    () => working.map((k) => byKey.get(k)).filter((i): i is ITabObject => !!i),
    [working, byKey]
  );

  const atCapacity = working.length >= maxPinned;

  const isDirty = useMemo(() => {
    if (working.length !== pinnedKeys.length) return true;
    return working.some((k, i) => k !== pinnedKeys[i]);
  }, [working, pinnedKeys]);

  const flashCapacity = () => {
    Toast.show({ content: "Remove an item to add another" });
    clearTimeout(shakeTimer.current);
    setShake(true);
    shakeTimer.current = setTimeout(() => setShake(false), 350);
  };

  const toggle = (key: string) =>
    setWorking((prev) => {
      if (prev.includes(key)) return prev.filter((k) => k !== key);
      if (prev.length >= maxPinned) {
        flashCapacity();
        return prev;
      }
      return [...prev, key];
    });

  const remove = (key: string) =>
    setWorking((prev) => prev.filter((k) => k !== key));

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    setWorking((prev) => {
      const from = prev.indexOf(active.id as string);
      const to = prev.indexOf(over.id as string);
      if (from === -1 || to === -1) return prev;
      return arrayMove(prev, from, to);
    });
  };

  const handleSave = () => {
    onSave(working);
    onClose();
  };

  return (
    <Popup
      visible={open}
      onMaskClick={onClose}
      onClose={onClose}
      bodyClassName={styles.popupBody}
      destroyOnClose
    >
      <div className={styles.sheet}>
        <div className={styles.header}>
          <button
            type="button"
            className={styles.closeBtn}
            aria-label="Close"
            onClick={onClose}
          >
            <CloseOutlined />
          </button>
          <div className={styles.title}>Customize Bar</div>
          <button
            type="button"
            className={styles.saveBtn}
            disabled={!isDirty}
            onClick={handleSave}
          >
            Save
          </button>
        </div>

        <div className={styles.scroll}>
          <p className={styles.description}>
            Pick up to <strong>{maxPinned} menu</strong> items for your bottom
            bar. Tap to add, tap again to remove.
          </p>

          <div className={styles.sectionHead}>
            <div>
              <div className={styles.sectionTitle}>Selected item</div>
              <div className={styles.sectionHint}>
                hold &amp; drag to reorder
              </div>
            </div>
            <span
              className={`${styles.countPill} ${
                atCapacity ? styles.countPillFull : ""
              } ${shake ? styles.countPillShake : ""}`}
            >
              {working.length} / {maxPinned}
            </span>
          </div>

          {selectedItems.length === 0 ? (
            <div className={styles.emptyHint}>
              Tap items below to add them to your bottom bar.
            </div>
          ) : (
            <div className={styles.selectedRow}>
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                modifiers={[restrictToHorizontalAxis, restrictToParentElement]}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={working}
                  strategy={horizontalListSortingStrategy}
                >
                  {selectedItems.map((item) => (
                    <SelectedCard
                      key={item.key as string}
                      item={item}
                      onRemove={remove}
                    />
                  ))}
                </SortableContext>
              </DndContext>
              {/* The bar always keeps a permanent "More" tab (opens this sheet). */}
              <div
                className={`${styles.selectedCard} ${styles.selectedCardGhost}`}
                aria-label="More tab (always shown)"
              >
                <span className={styles.selectedIcon}>
                  <AppstoreOutlined />
                </span>
                <span className={styles.selectedLabel}>More</span>
              </div>
            </div>
          )}

          <div className={styles.sectionTitle} style={{ marginTop: 24 }}>
            All menu items
          </div>
          <div className={styles.menuGrid}>
            {allItems.map((item) => {
              const key = item.key as string;
              const selected = working.includes(key);
              return (
                <MenuItem
                  key={key}
                  item={item}
                  selected={selected}
                  muted={!selected && atCapacity}
                  onToggle={toggle}
                />
              );
            })}
          </div>

          <button type="button" className={styles.resetBtn} onClick={onReset}>
            Reset to default layout
          </button>
        </div>
      </div>
    </Popup>
  );
};

export default MobileNavCustomizerSheet;
